import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { BaseChartProps, shapeMap } from './BaseChart';
import { DataPoint } from '../../../types/chart';

interface ScatterChartProps extends BaseChartProps {
  generateFitCurve?: (seriesData: DataPoint[], seriesName: string) => Promise<{x_raw: number, y_fit: number}[]>;
}

const ScatterChart: React.FC<ScatterChartProps> = ({
  data,
  seriesList,
  seriesInfo,
  seriesVisibility,
  xAxisLabel,
  yAxisLabel,
  xLogScale,
  yLogScale,
  xMinValue,
  xMaxValue,
  yMinValue,
  yMaxValue,
  fitResults,
  onSeriesClick,
  onVisibilityToggle,
  generateFitCurve
}) => {
  const chartRef = useRef<HTMLDivElement>(null);

  // 计算系列的统计信息（最大值、最小值、平均值）
  const calculateSeriesStats = (seriesData: DataPoint[]) => {
    if (seriesData.length === 0) return { min: 0, max: 0, avg: 0 };
    
    const yValues = seriesData.map(d => d.y);
    const min = Math.min(...yValues);
    const max = Math.max(...yValues);
    const sum = yValues.reduce((a, b) => a + b, 0);
    const avg = sum / yValues.length;
    
    return {
      min: parseFloat(min.toFixed(2)),
      max: parseFloat(max.toFixed(2)),
      avg: parseFloat(avg.toFixed(2))
    };
  };

  useEffect(() => {
    if (!chartRef.current) return;
    
    // 清除之前的图表和tooltip
    d3.select(chartRef.current).selectAll("*").remove();
    
    const margin = { top: 20, right: 200, bottom: 100, left: 60 }; // 增加底部边距
    const width = 700 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;
    
    // 创建SVG元素
    const svg = d3.select(chartRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // 检查是否有数据
    if (data.length === 0) {
      // 没有数据时显示提示文本
      svg.append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("fill", "#666")
        .text("没有可显示的数据。");
      
      // 即使没有数据，仍然添加坐标轴和图例
      createAxesAndLegend(svg, width, height, margin);
      return;
    }
    
    // 根据是否为对数坐标轴过滤数据
    const plotData = xLogScale ? data.filter(d => typeof d.x === 'number' && d.x > 0) : data;

    if (plotData.length === 0) {
      // 过滤后没有数据时显示提示文本
      svg.append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("fill", "#666")
        .text("没有可显示的数据。");
      
      // 即使没有数据，仍然添加坐标轴和图例
      createAxesAndLegend(svg, width, height, margin);
      return;
    }
    
    // 过滤出可见的数据
    const visibleData = plotData.filter(d => d.series && (seriesVisibility[d.series] !== false));
    
    // 设置坐标轴
    const xDomain = [xMinValue, xMaxValue];
    
    // 对于对数坐标轴，直接使用log后的值
    const xScale = xLogScale 
      ? d3.scaleLinear().domain(xDomain)
      : d3.scaleLinear().domain(xDomain);
    xScale.range([0, width]);
    
    const yScale = yLogScale
      ? d3.scaleLog().domain([Math.max(0.001, yMinValue), yMaxValue])
      : d3.scaleLinear().domain([yMinValue, yMaxValue]);
    yScale.range([height, 0]);
    
    // 添加X轴
    const xAxis = d3.axisBottom(xScale);
    
    // 对于对数坐标轴，使用自定义格式化
    if (xLogScale) {
      xAxis.tickFormat(d => `${d}`);
    }
    
    // 绘制x轴
    const xAxisGroup = svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis);

    // 旋转x轴刻度标签
    xAxisGroup.selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)");

    // 计算x轴标签的合适位置
    // 获取x轴高度 (包括刻度和旋转后的文本)
    const xAxisHeight = xAxisGroup.node()?.getBBox().height || 30;
    const xLabelDistance = xAxisHeight + 10; // 在x轴高度基础上增加10px的间距
    
    // 添加X轴标签
    // 计算标签宽度 - 根据内容估算
    const estimatedLabelWidth = xAxisLabel.length * 7; // 每个字符约7px
    const labelWidth = Math.max(150, Math.min(width, estimatedLabelWidth));
    
    const xLabelContainer = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left + width/2}, ${margin.top + height + xLabelDistance})`);
    
    // 支持HTML标签（包括上下标）
    xLabelContainer.append("foreignObject")
      .attr("width", labelWidth)
      .attr("height", 40) // 增加高度以容纳可能的换行
      .attr("x", -labelWidth/2)  // 居中
      .attr("y", 0)
      .append("xhtml:div")
      .style("text-align", "center")
      .style("font-size", "12px")
      .style("overflow-wrap", "break-word") // 允许长文本换行
      .html(xAxisLabel);
    
    // 添加Y轴
    const yAxis = d3.axisLeft(yScale);
    
    if (yLogScale) {
      yAxis.tickFormat((d: any) => {
        const log = Math.log10(d);
        return log === Math.floor(log) ? `10^${log}` : '';
      });
    } else {
      // 为线性刻度添加下标格式
      yAxis.tickFormat((d: any) => {
        return `${d}`;
      });
    }
    
    svg.append("g")
      .call(yAxis);
    
    // 添加Y轴标签
    const yLabelContainer = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left - 40}, ${margin.top + height/2}) rotate(-90)`);
    
    // 支持HTML标签（包括上下标）
    yLabelContainer.append("foreignObject")
      .attr("width", 200)
      .attr("height", 30)
      .attr("x", -100)  // 居中
      .attr("y", -15)
      .append("xhtml:div")
      .style("text-align", "center")
      .style("font-size", "12px")
      .html(yAxisLabel);
    
    // 如果没有可见数据，显示提示文本，但仍然显示图例
    if (visibleData.length === 0) {
      svg.append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("fill", "#666")
        .text("没有可见的数据。请在图例中选择要显示的系列。");
    }
    
    // 创建交互提示框
    const tooltip = d3.select(chartRef.current)
      .append("div")
      .attr("class", "chart-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("opacity", "0")
      .style("display", "none")
      .style("background", "white")
      .style("border", "1px solid #ddd")
      .style("border-radius", "4px")
      .style("padding", "8px")
      .style("box-shadow", "0 0 5px rgba(0,0,0,0.1)")
      .style("pointer-events", "none")
      .style("font-size", "12px")
      .style("z-index", "1000");
    
    // 辅助函数：获取系列的颜色
    const getSeriesColor = (series: string) => {
      return seriesInfo[series]?.color || "#000000";
    };
    
    // 辅助函数：获取系列的形状
    const getSeriesShape = (series: string) => {
      return seriesInfo[series]?.shape || "circle";
    };
    
    // 添加图例
    const legend = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left + width + 30}, ${margin.top})`);
    
    // 记录累计高度，以便垂直排列图例项
    let cumulativeHeight = 0;
    
    seriesList.forEach((series) => {
      const seriesData = plotData.filter(d => d.series === series);
      if (seriesData.length === 0) return;
      
      const color = getSeriesColor(series);
      const shapeType = shapeMap[getSeriesShape(series)] || d3.symbolCircle;
      const itemHeight = seriesInfo[series]?.description ? 40 : 20; // 调整高度，只包含系列名称和描述
      const isVisible = seriesVisibility[series] !== false;
      
      // 图例项容器
      const legendItem = legend.append("g")
        .attr("class", "legend-item")
        .attr("data-series", series)
        .attr("transform", `translate(0, ${cumulativeHeight})`)
        .style("cursor", "pointer")
        .on("click", () => {
          if (onSeriesClick) {
            onSeriesClick(series);
          }
        });
      
      // 添加可见性切换图标
      legendItem.append("text")
        .attr("class", "visibility-toggle-icon")
        .attr("x", -15)
        .attr("y", 14)
        .style("cursor", "pointer")
        .style("font-size", "14px")
        .text(isVisible ? '○' : '●')
        .on('click', function(event) {
          event.stopPropagation();
          if (onVisibilityToggle) {
            onVisibilityToggle(series);
          }
        });
      
      // 绘制图例标记
      const symbol = d3.symbol().size(40).type(shapeType);
      legendItem.append("path")
        .attr("d", symbol())
        .attr("transform", "translate(5, 10)")
        .style("fill", color)
        .style("stroke", "white")
        .style("stroke-width", 1);
      
      // 图例文本 - 系列名称
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 14)
        .attr("text-anchor", "start")
        .style("font-weight", "bold")
        .style("font-size", "12px")
        .style("fill", "#333")
        .html(seriesInfo[series]?.name || series);
      
      // 添加系列描述（EC50置信区间）
      if (seriesInfo[series]?.description) {
        legendItem.append("foreignObject")
          .attr("x", 15)
          .attr("y", 20)
          .attr("width", 150)
          .attr("height", 20)
          .append("xhtml:div")
          .style("font-size", "10px")
          .style("color", "#666")
          .html(seriesInfo[series]?.description || '');
      }
      
      // 更新累计高度
      cumulativeHeight += itemHeight + 10;
    });
    
    // 如果有可见数据，绘制散点图和拟合曲线
    if (visibleData.length > 0) {
      // 绘制拟合曲线函数
      const drawFitCurve = async (series: string, seriesData: DataPoint[]) => {
        if (!generateFitCurve) return;
        
        try {
          // 获取拟合曲线数据
          const fitCurveData = await generateFitCurve(seriesData, series);
          if (fitCurveData.length === 0) return;
          
          const color = getSeriesColor(series);
          
          // 创建拟合曲线的线条 - 对于对数坐标轴，直接使用log后的值
          const linePath = d3.line<{x_raw: number, y_fit: number}>()
            .x(d => xScale(xLogScale ? Math.log10(d.x_raw) : d.x_raw))
            .y(d => yScale(d.y_fit))
            .curve(d3.curveBasis); // 使用平滑曲线
          
          // 绘制拟合曲线
          svg.append("path")
            .datum(fitCurveData)
            .attr("fill", "none")
            .attr("stroke", color)
            .attr("stroke-width", 1.5)
            .attr("d", linePath);

          // 添加透明的宽线用于鼠标交互
          svg.append("path")
            .datum(fitCurveData)
            .attr("fill", "none")
            .attr("stroke", "transparent")
            .attr("stroke-width", 10) // 更宽的透明线条，便于鼠标悬停
            .attr("d", linePath)
            .style("cursor", "pointer")
            .on("mouseover", function(event: MouseEvent) {
              // 获取鼠标位置
              const [mouseX, mouseY] = d3.pointer(event, this);
              
              // 反向计算x值，从坐标到数据
              let xValue = xScale.invert(mouseX);
              
              // 如果使用了log刻度，进行反转换
              if (xLogScale) {
                xValue = Math.pow(10, xValue as number);
              }
              
              // 找到最接近的数据点
              const bisect = d3.bisector((d: {x_raw: number}) => d.x_raw).left;
              const index = bisect(fitCurveData, xValue as number);
              const dataPoint = index < fitCurveData.length ? fitCurveData[index] : fitCurveData[fitCurveData.length - 1];
              
              // 获取图表容器位置
              const chartRect = chartRef.current!.getBoundingClientRect();
              const clientX = event.clientX - chartRect.left;
              const clientY = event.clientY - chartRect.top;
              
              // 显示tooltip
              tooltip
                .style('display', 'block')
                .style('opacity', '1')
                .style('visibility', 'visible')
                .html(`
                  <strong>${series} (拟合曲线)</strong><br/>
                  x: ${dataPoint.x_raw.toFixed(2)}<br/>
                  y: ${dataPoint.y_fit.toFixed(2)}
                `)
                .style('left', `${clientX + 10}px`)
                .style('top', `${clientY - 30}px`);
            })
            .on("mousemove", function(event: MouseEvent) {
              // 与mouseover相同的计算逻辑
              const [mouseX, mouseY] = d3.pointer(event, this);
              
              let xValue = xScale.invert(mouseX);
              if (xLogScale) {
                xValue = Math.pow(10, xValue as number);
              }
              
              const bisect = d3.bisector((d: {x_raw: number}) => d.x_raw).left;
              const index = bisect(fitCurveData, xValue as number);
              const dataPoint = index < fitCurveData.length ? fitCurveData[index] : fitCurveData[fitCurveData.length - 1];
              
              const chartRect = chartRef.current!.getBoundingClientRect();
              const clientX = event.clientX - chartRect.left;
              const clientY = event.clientY - chartRect.top;
              
              tooltip
                .style('left', `${clientX + 10}px`)
                .style('top', `${clientY - 30}px`);
            })
            .on("mouseout", function() {
              tooltip
                .style('display', 'none')
                .style('opacity', '0')
                .style('visibility', 'hidden');
            });
        } catch (error) {
          console.error(`绘制系列 ${series} 拟合曲线出错:`, error);
        }
      };
      
      // 绘制散点函数
      const drawSeriesPoints = (series: string, seriesData: DataPoint[]) => {
        if (!seriesData || seriesData.length === 0) return;
        
        const color = getSeriesColor(series);
        const shapeType = shapeMap[getSeriesShape(series)] || d3.symbolCircle;
        const symbol = d3.symbol().size(60).type(shapeType);
        
        // 绘制散点 - 使用不同形状
        svg.selectAll(null)
          .data(seriesData)
          .enter()
          .append("path")
          .attr("d", symbol)
          .attr("transform", d => {
            // 对于对数坐标轴，直接使用log后的值
            const xValue = typeof d.x === 'number' ? (xLogScale ? Math.log10(d.x) : d.x) : 0;
            return `translate(${xScale(xValue)}, ${yScale(d.y)})`;
          })
          .attr("fill", color)
          .attr("stroke", "white")
          .attr("stroke-width", 0.5)
          .on('mouseover', function(event: MouseEvent, d: DataPoint) {
            const chartRect = chartRef.current!.getBoundingClientRect();
            const mouseX = event.clientX - chartRect.left;
            const mouseY = event.clientY - chartRect.top;
            
            tooltip
              .style('display', 'block')
              .style('opacity', '1')
              .style('visibility', 'visible')
              .html(`
                <strong>${series}</strong><br/>
                x: ${typeof d.x === 'number' ? d.x.toFixed(2) : d.x}<br/>
                y: ${d.y.toFixed(2)}
              `)
              .style('left', `${mouseX + 10}px`)
              .style('top', `${mouseY - 30}px`);
          })
          .on('mousemove', function(event: MouseEvent) {
            const chartRect = chartRef.current!.getBoundingClientRect();
            const mouseX = event.clientX - chartRect.left;
            const mouseY = event.clientY - chartRect.top;
            
            tooltip
              .style('left', `${mouseX + 10}px`)
              .style('top', `${mouseY - 30}px`);
          })
          .on('mouseout', function() {
            tooltip
              .style('display', 'none')
              .style('opacity', '0')
              .style('visibility', 'hidden');
          });
      };

      // 先绘制拟合曲线，再绘制散点
      const drawAllSeries = async () => {
        // 首先绘制所有拟合曲线
        for (const series of seriesList) {
          if (seriesVisibility[series] === false) continue;
          const seriesData = plotData.filter(d => d.series === series);
          if (seriesData.length > 0) {
            await drawFitCurve(series, seriesData);
          }
        }
        
        // 然后绘制所有散点
        for (const series of seriesList) {
          if (seriesVisibility[series] === false) continue;
          const seriesData = plotData.filter(d => d.series === series);
          if (seriesData.length > 0) {
            drawSeriesPoints(series, seriesData);
          }
        }
      };
      
      // 使用Promise.all同时处理所有系列，避免依次等待
      (async () => {
        try {
          await drawAllSeries();
        } catch (error) {
          console.error("绘制图表时发生错误:", error);
        }
      })();
    }

  }, [data, seriesList, seriesInfo, seriesVisibility, xAxisLabel, yAxisLabel, xLogScale, yLogScale, xMinValue, xMaxValue, yMinValue, yMaxValue, fitResults, onSeriesClick, onVisibilityToggle, generateFitCurve]);

  // 辅助函数：创建坐标轴和图例
  const createAxesAndLegend = (svg: d3.Selection<SVGGElement, unknown, null, undefined>, width: number, height: number, margin: {top: number, right: number, bottom: number, left: number}) => {
    // 设置坐标轴
    const xDomain = [xMinValue, xMaxValue];
    const xScale = xLogScale 
      ? d3.scaleLinear().domain(xDomain)
      : d3.scaleLinear().domain(xDomain);
    xScale.range([0, width]);
    
    const yScale = yLogScale
      ? d3.scaleLog().domain([Math.max(0.001, yMinValue), yMaxValue])
      : d3.scaleLinear().domain([yMinValue, yMaxValue]);
    yScale.range([height, 0]);
    
    // 添加X轴
    const xAxis = d3.axisBottom(xScale);
    
    if (xLogScale) {
      xAxis.tickFormat(d => `${d}`);
    }
    
    // 绘制x轴
    const xAxisGroup = svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis);

    // 旋转x轴刻度标签
    xAxisGroup.selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)");

    // 计算x轴标签的合适位置
    // 获取x轴高度 (包括刻度和旋转后的文本)
    const xAxisHeight = xAxisGroup.node()?.getBBox().height || 30;
    const xLabelDistance = xAxisHeight + 10; // 在x轴高度基础上增加10px的间距
    
    // 添加X轴标签
    // 计算标签宽度 - 根据内容估算
    const estimatedLabelWidth = xAxisLabel.length * 7; // 每个字符约7px
    const labelWidth = Math.max(150, Math.min(width, estimatedLabelWidth));
    
    const xLabelContainer = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left + width/2}, ${margin.top + height + xLabelDistance})`);
    
    // 支持HTML标签（包括上下标）
    xLabelContainer.append("foreignObject")
      .attr("width", labelWidth)
      .attr("height", 40) // 增加高度以容纳可能的换行
      .attr("x", -labelWidth/2)  // 居中
      .attr("y", 0)
      .append("xhtml:div")
      .style("text-align", "center")
      .style("font-size", "12px")
      .style("overflow-wrap", "break-word") // 允许长文本换行
      .html(xAxisLabel);
    
    // 添加Y轴
    const yAxis = d3.axisLeft(yScale);
    
    if (yLogScale) {
      yAxis.tickFormat((d: any) => {
        const log = Math.log10(d);
        return log === Math.floor(log) ? `10^${log}` : '';
      });
    } else {
      // 为线性刻度添加下标格式
      yAxis.tickFormat((d: any) => {
        return `${d}`;
      });
    }
    
    svg.append("g")
      .call(yAxis);
    
    // 添加Y轴标签
    const yLabelContainer = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left - 40}, ${margin.top + height/2}) rotate(-90)`);
    
    // 支持HTML标签（包括上下标）
    yLabelContainer.append("foreignObject")
      .attr("width", 200)
      .attr("height", 30)
      .attr("x", -100)  // 居中
      .attr("y", -15)
      .append("xhtml:div")
      .style("text-align", "center")
      .style("font-size", "12px")
      .html(yAxisLabel);
    
    // 添加图例
    const legend = d3.select(chartRef.current)
      .select("svg")
      .append("g")
      .attr("transform", `translate(${margin.left + width + 30}, ${margin.top})`);
    
    // 辅助函数：获取系列的颜色
    const getSeriesColor = (series: string) => {
      return seriesInfo[series]?.color || "#000000";
    };
    
    // 辅助函数：获取系列的形状
    const getSeriesShape = (series: string) => {
      return seriesInfo[series]?.shape || "circle";
    };
    
    // 记录累计高度，以便垂直排列图例项
    let cumulativeHeight = 0;
    
    seriesList.forEach((series) => {
      const color = getSeriesColor(series);
      const shapeType = shapeMap[getSeriesShape(series)] || d3.symbolCircle;
      const itemHeight = seriesInfo[series]?.description ? 40 : 20; // 调整高度，只包含系列名称和描述
      const isVisible = seriesVisibility[series] !== false;
      
      // 图例项容器
      const legendItem = legend.append("g")
        .attr("class", "legend-item")
        .attr("data-series", series)
        .attr("transform", `translate(0, ${cumulativeHeight})`)
        .style("cursor", "pointer")
        .on("click", () => {
          if (onSeriesClick) {
            onSeriesClick(series);
          }
        });
      
      // 添加可见性切换图标
      legendItem.append("text")
        .attr("class", "visibility-toggle-icon")
        .attr("x", -15)
        .attr("y", 14)
        .style("cursor", "pointer")
        .style("font-size", "14px")
        .text(isVisible ? '○' : '●')
        .on('click', function(event) {
          event.stopPropagation();
          if (onVisibilityToggle) {
            onVisibilityToggle(series);
          }
        });
      
      // 绘制图例标记
      const symbol = d3.symbol().size(40).type(shapeType);
      legendItem.append("path")
        .attr("d", symbol())
        .attr("transform", "translate(5, 10)")
        .style("fill", color)
        .style("stroke", "white")
        .style("stroke-width", 1);
      
      // 图例文本 - 系列名称
      legendItem.append("text")
        .attr("x", 15)
        .attr("y", 14)
        .attr("text-anchor", "start")
        .style("font-weight", "bold")
        .style("font-size", "12px")
        .style("fill", "#333")
        .html(seriesInfo[series]?.name || series);
      
      // 添加系列描述（EC50置信区间）
      if (seriesInfo[series]?.description) {
        legendItem.append("foreignObject")
          .attr("x", 15)
          .attr("y", 20)
          .attr("width", 150)
          .attr("height", 20)
          .append("xhtml:div")
          .style("font-size", "10px")
          .style("color", "#666")
          .html(seriesInfo[series]?.description || '');
      }
      
      // 更新累计高度
      cumulativeHeight += itemHeight + 10;
    });
  };

  return <div ref={chartRef} style={{ width: '100%', height: '100%' }}></div>;
};

export default ScatterChart;